import { useQuery } from '@tanstack/react-query';
import type { BriefTheme } from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { SourceItemList } from '../sources/SourceItemList';

/**
 * "View sources" for a Social Trends card — lists the SourceItems that the brief
 * clustered into this theme. Fetches on open via the theme's stored item ids.
 */
export function ThemeSourcesModal({
  briefId,
  theme,
  onClose,
}: {
  briefId: string | null;
  theme: BriefTheme | null;
  onClose: () => void;
}) {
  const open = briefId != null && theme != null;

  const sources = useQuery({
    queryKey: ['theme-sources', briefId, theme?.id],
    queryFn: ({ signal }) => api.briefThemeSources(briefId!, theme!.id, signal),
    enabled: open,
    staleTime: 0,
  });

  const list = sources.data ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={theme ? `Sources · ${theme.title}` : 'Sources'}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-3">
        {sources.isLoading ? (
          <LoadingState label="Loading sources…" />
        ) : sources.isError ? (
          <ErrorState
            message={
              sources.error instanceof ApiRequestError
                ? sources.error.message
                : 'Could not load sources.'
            }
            onRetry={() => void sources.refetch()}
          />
        ) : list.length === 0 ? (
          <EmptyState
            title="No sources recorded"
            description="This theme has no linked source items. Regenerate the digest to refresh its sources."
          />
        ) : (
          <>
            <p className="text-xs text-ink-faint">
              {list.length} source item{list.length === 1 ? '' : 's'} fed this theme.
            </p>
            <SourceItemList items={list} />
          </>
        )}
      </div>
    </Modal>
  );
}
